import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ChevronLeft, ChevronRight, Lock, RefreshCw } from "lucide-react";
import { API } from "@/lib/constants";

/**
 * Phase 5 Day 3-5 — Calibration view.
 *
 * Spec ref: PHASE_5_VLM_CRITIC_IMPLEMENTATION_SPEC.md § 4.2.
 *
 * Side-by-side labelling UI for the focal-point + hierarchy dimensions.
 * Operator labels each holdout card pass/fail per dimension; once ~20
 * are labelled, the threshold-proposal block surfaces and the operator
 * can lock thresholds (writes vlm_critic_config + history rows).
 *
 * Discipline pinned by the playbook (PHASE_5_CALIBRATION_PLAYBOOK.md):
 *  - critic verdict column is collapsible so the operator can decide
 *    without peeking at the critic's IoU first.
 *  - skips persist; previous/next nav doesn't lose unsaved state
 *    because every label fires its own /label POST.
 */
export default function CalibrationView({ user }) {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const [holdout, setHoldout] = useState([]);
  const [labels, setLabels] = useState({});  // {card_id: {dim: label}}
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [peekCritic, setPeekCritic] = useState(false);
  const [focalProposal, setFocalProposal] = useState(null);
  const [hierarchyProposal, setHierarchyProposal] = useState(null);
  const [locking, setLocking] = useState(false);

  const labelledBy = user?.email || "unknown@calibration";

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [holdoutRes, labelsRes] = await Promise.all([
        fetch(`${API}/v2/workspaces/${workspaceId}/critic/calibration/holdout?limit=20`, { credentials: "include" }),
        fetch(`${API}/v2/workspaces/${workspaceId}/critic/calibration/labels?labelled_by=${encodeURIComponent(labelledBy)}`, { credentials: "include" }),
      ]);
      const holdoutJson = holdoutRes.ok ? await holdoutRes.json() : [];
      const labelsJson = labelsRes.ok ? await labelsRes.json() : [];
      setHoldout(holdoutJson);
      const grouped = {};
      for (const l of labelsJson) {
        if (!grouped[l.card_id]) grouped[l.card_id] = {};
        grouped[l.card_id][l.dimension] = l.label_value;
      }
      setLabels(grouped);
    } catch (e) {
      console.error("calibration fetch failed", e);
      toast.error("Failed to load calibration data");
    } finally {
      setLoading(false);
    }
  }, [workspaceId, labelledBy]);

  const fetchProposals = useCallback(async () => {
    try {
      const [fp, hr] = await Promise.all([
        fetch(`${API}/v2/workspaces/${workspaceId}/critic/calibration/propose-threshold/focal_point?labelled_by=${encodeURIComponent(labelledBy)}`, { credentials: "include" }),
        fetch(`${API}/v2/workspaces/${workspaceId}/critic/calibration/propose-threshold/hierarchy?labelled_by=${encodeURIComponent(labelledBy)}`, { credentials: "include" }),
      ]);
      setFocalProposal(fp.ok ? await fp.json() : null);
      setHierarchyProposal(hr.ok ? await hr.json() : null);
    } catch (e) {
      console.error("proposal fetch failed", e);
    }
  }, [workspaceId, labelledBy]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { fetchProposals(); }, [labels, fetchProposals]);

  const card = holdout[idx];
  const cardLabels = card ? (labels[card.card_id] || {}) : {};

  const submitLabel = async (dimension, label_value) => {
    if (!card) return;
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/critic/calibration/label`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          card_id: card.card_id, dimension, label_value, labelled_by: labelledBy,
        }),
      });
      if (!r.ok) throw new Error(`status ${r.status}`);
      setLabels((prev) => ({
        ...prev,
        [card.card_id]: { ...(prev[card.card_id] || {}), [dimension]: label_value },
      }));
      toast.success(`${dimension}: ${label_value}`);
    } catch (e) {
      console.error("label submit failed", e);
      toast.error("Could not save label");
    }
  };

  const lockThreshold = async (dimension, proposal) => {
    if (!proposal) return;
    setLocking(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/critic/calibration/lock`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dimension,
          threshold_value: proposal.threshold_value,
          locked_by: labelledBy,
          sample_n: proposal.sample_n,
          accuracy_at_lock: proposal.accuracy,
          rationale: `Locked from calibration view at accuracy=${(proposal.accuracy * 100).toFixed(0)}%`,
        }),
      });
      if (!r.ok) throw new Error(`status ${r.status}`);
      toast.success(`${dimension} threshold locked at ${proposal.threshold_value}`);
      await fetchProposals();
    } catch (e) {
      console.error("lock failed", e);
      toast.error("Could not lock threshold");
    } finally {
      setLocking(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-sm text-slate-500" data-testid="calibration-loading">
        Loading calibration data…
      </div>
    );
  }

  if (holdout.length === 0) {
    return (
      <div className="max-w-2xl mx-auto p-8 space-y-4" data-testid="calibration-empty">
        <button
          onClick={() => navigate(-1)}
          className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
        >
          <ArrowLeft className="h-3 w-3" /> Back
        </button>
        <h1 className="text-2xl font-semibold text-slate-900">Calibration</h1>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-semibold">No holdout cards yet.</p>
          <p className="mt-2 text-xs text-amber-800">
            The critic needs to assess at least one published card before
            calibration can start. The auto-assess hook is not yet enabled;
            until then, run the focal-point / hierarchy critic manually
            against a published card to populate the holdout.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
          data-testid="calibration-back-btn"
        >
          <ArrowLeft className="h-3 w-3" /> Back
        </button>
        <div className="text-xs text-slate-600 tabular-nums">
          Card {idx + 1} / {holdout.length}
        </div>
        <button
          onClick={fetchAll}
          className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
          data-testid="calibration-refresh-btn"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      <h1 className="text-2xl font-semibold text-slate-900">VLM Critic — Calibration</h1>
      <p className="text-xs text-slate-600 max-w-xl">
        Label each card pass/fail per dimension. Try to decide BEFORE peeking
        at the critic verdict. After ~20 labels, threshold proposals surface
        below.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: rendered card */}
        <div
          className="rounded-lg border border-slate-200 bg-white p-4"
          data-testid="calibration-card-preview"
        >
          <div className="text-[10px] uppercase font-semibold text-slate-500 tracking-wider mb-2">
            Rendered card
          </div>
          {card.published_image_url ? (
            <img
              src={card.published_image_url}
              alt={card.title || "card preview"}
              className="w-full rounded border border-slate-200"
            />
          ) : (
            <div className="aspect-square flex items-center justify-center bg-slate-100 rounded text-xs text-slate-400">
              No render available
            </div>
          )}
          <div className="mt-3 text-xs text-slate-700">
            <div className="font-semibold">{card.title || "(untitled)"}</div>
            <div className="text-slate-500 mt-1">
              {card.editorial_register || "no register"} · {card.render_mode || "default"}
            </div>
          </div>
        </div>

        {/* Right: critic output (collapsible) */}
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase font-semibold text-slate-500 tracking-wider">
              Critic output
            </div>
            <button
              type="button"
              onClick={() => setPeekCritic((v) => !v)}
              data-testid="calibration-peek-critic-toggle"
              className="text-[10px] text-slate-500 hover:text-slate-700 underline"
            >
              {peekCritic ? "Hide" : "Peek (after deciding)"}
            </button>
          </div>
          {peekCritic ? (
            <div className="space-y-3 text-xs">
              {["focal_point", "hierarchy"].map((dim) => {
                const v = card.verdicts?.[dim];
                if (!v) {
                  return (
                    <div key={dim} className="rounded border border-slate-200 px-3 py-2 text-slate-500">
                      <div className="font-semibold text-slate-700">{dim.replace("_", " ")}</div>
                      <div className="mt-1">No critic verdict yet for this card.</div>
                    </div>
                  );
                }
                const env = v.envelope || {};
                return (
                  <div
                    key={dim}
                    data-testid={`calibration-critic-output-${dim}`}
                    className="rounded border border-slate-200 px-3 py-2"
                  >
                    <div className="font-semibold text-slate-700">{dim.replace("_", " ")}</div>
                    {dim === "focal_point" && env.iou !== undefined && (
                      <div className="mt-1 text-slate-600">IoU: {env.iou?.toFixed?.(3)}</div>
                    )}
                    {dim === "hierarchy" && env.edit_distance !== undefined && (
                      <div className="mt-1 text-slate-600">
                        Edit distance: {env.edit_distance} ·
                        {(env.perceived_order || []).join(" → ") || "—"}
                      </div>
                    )}
                    {env.rationale && (
                      <div className="mt-1 text-slate-500 italic">{env.rationale}</div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-xs text-slate-400 italic py-6 text-center">
              Decide your label first. Click "Peek (after deciding)" only to verify.
            </div>
          )}
        </div>
      </div>

      {/* Operator label band */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
        <div className="text-[10px] uppercase font-semibold text-slate-500 tracking-wider">
          Your label
        </div>

        {["focal_point", "hierarchy"].map((dim) => {
          const current = cardLabels[dim] || null;
          return (
            <div key={dim} className="flex items-center gap-3">
              <div className="text-xs font-semibold text-slate-700 w-32">
                {dim === "focal_point" ? "Focal point" : "Hierarchy"}
              </div>
              <button
                type="button"
                onClick={() => submitLabel(dim, "pass")}
                data-testid={`calibration-label-${dim}-pass`}
                className={`px-3 py-1 text-xs rounded border ${
                  current === "pass"
                    ? "bg-emerald-100 border-emerald-400 text-emerald-900 font-semibold"
                    : "bg-white border-slate-300 text-slate-600 hover:bg-emerald-50"
                }`}
              >
                ✓ {dim === "focal_point" ? "Lands right" : "Reads right"}
              </button>
              <button
                type="button"
                onClick={() => submitLabel(dim, "fail")}
                data-testid={`calibration-label-${dim}-fail`}
                className={`px-3 py-1 text-xs rounded border ${
                  current === "fail"
                    ? "bg-amber-100 border-amber-400 text-amber-900 font-semibold"
                    : "bg-white border-slate-300 text-slate-600 hover:bg-amber-50"
                }`}
              >
                ✗ Doesn&apos;t
              </button>
              <button
                type="button"
                onClick={() => submitLabel(dim, "skip")}
                data-testid={`calibration-label-${dim}-skip`}
                className={`px-3 py-1 text-xs rounded border ${
                  current === "skip"
                    ? "bg-slate-200 border-slate-400 text-slate-700 font-semibold"
                    : "bg-white border-slate-300 text-slate-600 hover:bg-slate-100"
                }`}
              >
                Skip
              </button>
            </div>
          );
        })}

        <div className="flex items-center justify-between pt-3 border-t border-slate-200">
          <button
            type="button"
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            disabled={idx === 0}
            data-testid="calibration-prev-btn"
            className="flex items-center gap-1 px-3 py-1 text-xs rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            <ChevronLeft className="h-3 w-3" /> Previous
          </button>
          <button
            type="button"
            onClick={() => setIdx((i) => Math.min(holdout.length - 1, i + 1))}
            disabled={idx === holdout.length - 1}
            data-testid="calibration-next-btn"
            className="flex items-center gap-1 px-3 py-1 text-xs rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            Next <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Threshold proposal block — surfaces once labels exist */}
      {(focalProposal || hierarchyProposal) && (
        <div
          className="rounded-lg border border-slate-300 bg-white p-4 space-y-3"
          data-testid="calibration-threshold-proposals"
        >
          <div className="text-[10px] uppercase font-semibold text-slate-500 tracking-wider">
            Threshold proposals
          </div>

          {focalProposal && (
            <div className="flex items-center justify-between gap-3" data-testid="calibration-proposal-focal-point">
              <div className="text-xs">
                <span className="font-semibold">Focal point</span> IoU &gt;= {focalProposal.threshold_value}
                <span className="text-slate-500">
                  {" "}(matches your labels {Math.round(focalProposal.accuracy * focalProposal.sample_n)}/{focalProposal.sample_n} = {Math.round(focalProposal.accuracy * 100)}%)
                </span>
              </div>
              <button
                type="button"
                onClick={() => lockThreshold("focal_point", focalProposal)}
                disabled={locking || focalProposal.accuracy < 0.85}
                data-testid="calibration-lock-focal-point-btn"
                className="flex items-center gap-1 px-3 py-1 text-xs rounded border border-blue-400 bg-blue-50 text-blue-900 hover:bg-blue-100 disabled:opacity-50 font-medium"
                title={focalProposal.accuracy < 0.85 ? "Need ≥85% agreement to lock" : "Lock this threshold"}
              >
                <Lock className="h-3 w-3" /> Lock
              </button>
            </div>
          )}

          {hierarchyProposal && (
            <div className="flex items-center justify-between gap-3" data-testid="calibration-proposal-hierarchy">
              <div className="text-xs">
                <span className="font-semibold">Hierarchy</span> edit_dist &lt;= {hierarchyProposal.threshold_value}
                <span className="text-slate-500">
                  {" "}(matches your labels {Math.round(hierarchyProposal.accuracy * hierarchyProposal.sample_n)}/{hierarchyProposal.sample_n} = {Math.round(hierarchyProposal.accuracy * 100)}%)
                </span>
              </div>
              <button
                type="button"
                onClick={() => lockThreshold("hierarchy", hierarchyProposal)}
                disabled={locking || hierarchyProposal.accuracy < 0.85}
                data-testid="calibration-lock-hierarchy-btn"
                className="flex items-center gap-1 px-3 py-1 text-xs rounded border border-blue-400 bg-blue-50 text-blue-900 hover:bg-blue-100 disabled:opacity-50 font-medium"
                title={hierarchyProposal.accuracy < 0.85 ? "Need ≥85% agreement to lock" : "Lock this threshold"}
              >
                <Lock className="h-3 w-3" /> Lock
              </button>
            </div>
          )}

          <p className="text-[10px] text-slate-500">
            Locks write to vlm_critic_config and vlm_critic_calibration_history.
            Locking is gated on ≥85% operator agreement (per spec § 4).
          </p>
        </div>
      )}
    </div>
  );
}
